import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Button,
  Table,
  Badge,
  Tabs,
  Modal,
  Stack,
  Text,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEye, IconCheck, IconX, IconShoppingCart } from '@tabler/icons-react';
import {
  useQuotes,
  useQuoteDetail,
  useAcceptQuote,
  useRejectQuote,
  useConvertQuote,
} from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  pending: 'gray',
  sent: 'blue',
  accepted: 'green',
  expired: 'red',
  rejected: 'orange',
};

export default function QuotesPage() {
  const [activeTab, setActiveTab] = useState<string | null>('all');
  const [detailOpened, detailHandlers] = useDisclosure(false);
  const [confirmOpened, confirmHandlers] = useDisclosure(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject' | null>(null);

  const statusFilter = activeTab === 'all' ? undefined : activeTab ?? undefined;
  const { data: quotes, isLoading } = useQuotes(statusFilter);
  const { data: quoteDetail } = useQuoteDetail(selectedQuoteId);
  const acceptQuote = useAcceptQuote();
  const rejectQuote = useRejectQuote();
  const convertQuote = useConvertQuote();

  const handleViewDetail = (id: number) => {
    setSelectedQuoteId(id);
    detailHandlers.open();
  };

  const handleAccept = (id: number) => {
    setSelectedQuoteId(id);
    setConfirmAction('accept');
    confirmHandlers.open();
  };

  const handleReject = (id: number) => {
    setSelectedQuoteId(id);
    setConfirmAction('reject');
    confirmHandlers.open();
  };

  const handleConfirm = async () => {
    if (!selectedQuoteId || !confirmAction) return;

    try {
      if (confirmAction === 'accept') {
        await acceptQuote.mutateAsync(selectedQuoteId);
        notifications.show({
          title: 'Success',
          message: 'Quote accepted successfully',
          color: 'green',
        });
      } else {
        await rejectQuote.mutateAsync(selectedQuoteId);
        notifications.show({
          title: 'Success',
          message: 'Quote rejected',
          color: 'blue',
        });
      }
      confirmHandlers.close();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: `Failed to ${confirmAction} quote`,
        color: 'red',
      });
    }
  };

  const handleConvertToOrder = async (id: number) => {
    try {
      await convertQuote.mutateAsync(id);
      notifications.show({
        title: 'Success',
        message: 'Quote converted to order successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to convert quote to order',
        color: 'red',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>My Quotes</Title>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="all">All</Tabs.Tab>
          <Tabs.Tab value="pending">Pending</Tabs.Tab>
          <Tabs.Tab value="sent">Sent</Tabs.Tab>
          <Tabs.Tab value="accepted">Accepted</Tabs.Tab>
          <Tabs.Tab value="expired">Expired</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={activeTab || 'all'} pt="md">
          {isLoading ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : !quotes || quotes.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              No quotes found
            </Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Quote #</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Expiry</Table.Th>
                  <Table.Th>Items</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {quotes.map((quote: any) => (
                  <Table.Tr key={quote.id}>
                    <Table.Td>Q-{quote.id}</Table.Td>
                    <Table.Td>{new Date(quote.created_at).toLocaleDateString()}</Table.Td>
                    <Table.Td>{new Date(quote.expiry_date).toLocaleDateString()}</Table.Td>
                    <Table.Td>{quote.items_count || 0}</Table.Td>
                    <Table.Td>{formatCurrency(quote.total_amount)}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLORS[quote.status]} variant="light">
                        {quote.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleViewDetail(quote.id)}
                        >
                          View
                        </Button>
                        {quote.status === 'sent' && (
                          <>
                            <Button
                              size="xs"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => handleAccept(quote.id)}
                            >
                              Accept
                            </Button>
                            <Button
                              size="xs"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => handleReject(quote.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={detailOpened}
        onClose={detailHandlers.close}
        title={`Quote Q-${selectedQuoteId} Details`}
        size="lg"
      >
        {quoteDetail ? (
          <Stack>
            <Group>
              <Text fw={600}>Status:</Text>
              <Badge color={STATUS_COLORS[quoteDetail.status]} variant="light">
                {quoteDetail.status}
              </Badge>
            </Group>
            <Group>
              <Text fw={600}>Date:</Text>
              <Text>{new Date(quoteDetail.created_at).toLocaleDateString()}</Text>
            </Group>
            <Group>
              <Text fw={600}>Expiry:</Text>
              <Text>{new Date(quoteDetail.expiry_date).toLocaleDateString()}</Text>
            </Group>

            {quoteDetail.items && quoteDetail.items.length > 0 && (
              <div>
                <Text fw={600} mb="xs">
                  Items:
                </Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Qty</Table.Th>
                      <Table.Th>Unit Price</Table.Th>
                      <Table.Th>Discount</Table.Th>
                      <Table.Th>Extension</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {quoteDetail.items.map((item: any) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>{item.product_name}</Table.Td>
                        <Table.Td>{item.quantity}</Table.Td>
                        <Table.Td>{formatCurrency(item.unit_price)}</Table.Td>
                        <Table.Td>{item.discount_percent}%</Table.Td>
                        <Table.Td>{formatCurrency(item.extension)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}

            <Group justify="flex-end">
              <Text fw={600}>Subtotal:</Text>
              <Text>{formatCurrency(quoteDetail.subtotal)}</Text>
            </Group>
            <Group justify="flex-end">
              <Text fw={600}>Tax:</Text>
              <Text>{formatCurrency(quoteDetail.tax_amount)}</Text>
            </Group>
            <Group justify="flex-end">
              <Text fw={600} size="lg">
                Total:
              </Text>
              <Text size="lg" fw={700}>
                {formatCurrency(quoteDetail.total_amount)}
              </Text>
            </Group>

            {quoteDetail.notes && (
              <div>
                <Text fw={600}>Notes:</Text>
                <Text>{quoteDetail.notes}</Text>
              </div>
            )}

            {quoteDetail.status === 'accepted' && (
              <Button
                leftSection={<IconShoppingCart size={16} />}
                onClick={() => handleConvertToOrder(quoteDetail.id)}
                loading={convertQuote.isPending}
              >
                Convert to Order
              </Button>
            )}
          </Stack>
        ) : (
          <Skeleton height={300} />
        )}
      </Modal>

      <Modal
        opened={confirmOpened}
        onClose={confirmHandlers.close}
        title={`${confirmAction === 'accept' ? 'Accept' : 'Reject'} Quote`}
        size="md"
      >
        <Stack>
          <Text>
            Are you sure you want to {confirmAction} quote Q-{selectedQuoteId}?
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={confirmHandlers.close}>
              Cancel
            </Button>
            <Button
              color={confirmAction === 'accept' ? 'green' : 'red'}
              onClick={handleConfirm}
              loading={acceptQuote.isPending || rejectQuote.isPending}
            >
              {confirmAction === 'accept' ? 'Accept' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
